import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, CalendarDays, Clock, CheckSquare,
  BarChart3, LogOut, Menu, X, ClipboardList, UserCircle
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_emp-management-9/artifacts/wiqwkhla_Untitled%20design%20%286%29.jpg";

const adminNav = [
  { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/admin/employees", label: "Employees", icon: Users },
  { path: "/admin/leaves", label: "Leave Management", icon: CalendarDays },
  { path: "/admin/attendance", label: "Attendance", icon: Clock },
  { path: "/admin/tasks", label: "Task Management", icon: CheckSquare },
  { path: "/admin/reports", label: "Reports", icon: BarChart3 },
];

const employeeNav = [
  { path: "/employee/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/employee/attendance", label: "Time Tracking", icon: Clock },
  { path: "/employee/leaves", label: "Leave Requests", icon: CalendarDays },
  { path: "/employee/tasks", label: "My Tasks", icon: ClipboardList },
];

const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = user?.role === "admin" ? adminNav : employeeNav;
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#1a2332] flex flex-col
          transition-transform duration-300 ease-in-out
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-slate-700/50 shrink-0">
          <img src={LOGO} alt="AppDroid" className="h-10 w-auto object-contain rounded" />
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b border-slate-700/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#38b6e8]/20 flex items-center justify-center shrink-0">
              <span className="text-[#38b6e8] text-sm font-bold">{initials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{user?.name || user?.email?.split("@")[0]}</p>
              <p className="text-slate-400 text-xs capitalize">{user?.role === "admin" ? "Administrator" : user?.employee?.job_role || "Employee"}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map(({ path, label, icon: Icon, end: isEnd }) => (
            <NavLink
              key={path}
              to={path}
              end={isEnd}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 mx-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all duration-200
                ${isActive
                  ? "bg-[#38b6e8]/15 text-white border-r-2 border-[#38b6e8]"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/40"
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={17} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-700/50 shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400
              hover:text-red-400 hover:bg-red-500/10 transition-all text-sm font-medium"
            data-testid="logout-button"
          >
            <LogOut size={17} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col md:ml-64 min-h-0">
        {/* Mobile topbar */}
        <header className="md:hidden h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-3 shrink-0">
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-button">
            <Menu size={20} className="text-slate-700" />
          </button>
          <img src={LOGO} alt="AppDroid" className="h-8 w-auto object-contain" />
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
